import { API_URL } from "env";
import { toast } from "react-toastify";

interface PromotionPriceInfo {
    percent?: number;
    price_promotion?: number | string | null;
}

interface PromotionFlashSaleInfo {
    percent?: number;
    price_flash_sale?: number | string | null;
}

interface CartItemsEnvelopeResponse {
    status?: boolean;
    data?: CartItemsResponse;
    msg?: string;
}

export interface CartProduct {
    id: number;
    name: string;
    slug: string;
    avatar: string;
    alt: string;
    product_category_id: number;
    price: number;
    discount: number;
    content: string;
    short_content: string;
    is_active: number;
    is_stock: number;
    star: number;
    discount_price?: number | string | null;
    packaging_spec: string;
    product_category: {
        id: number;
        name: string;
        slug: string;
    };
    promotion?: {
        type: string; // FLASH_SALE  || PROMOTION 
        percent: number;
        detail: any;
    } | null;
    promotion_product?: {
        product_id: number;
        promotion_id: number;
        percent: number;
        price_promotion?: number | string | null;
        promotion: {
            id: number;
            name: string;
            slug: string;
            start_date: string;
            end_date: string;
            is_active: number;
            created_at: string;
            updated_at: string;
        };
    }[];
    promotion_flash_sale_product?: {
        product_id: number;
        promotion_flash_sale_id: number;
        percent: number;
        price_flash_sale?: number | string | null;
        promotion_flash_sale?: {
            name: string;
            start_time?: string;
            end_time?: string;
        } | null;
    }[];
}
export interface CartItems {
    id: number;
    customer_id: number;
    product_id: number;
    is_check: number;
    quantity: number;
    created_at: string;
    updated_at: string;
    product: CartProduct;
}

export interface CartItemsResponse { items: CartItems[], current_page: number, total: number }

function parsePriceValue(value?: number | string | null): number | undefined {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }

    if (typeof value === 'string') {
        const normalized = value.replace(/\./g, '').replace(/,/g, '.').trim();
        const parsed = parseFloat(normalized);

        if (Number.isFinite(parsed)) {
            return parsed;
        }
    }

    return undefined;
}

function resolveDiscountPrice(product: CartProduct, originalPrice: number): number | undefined {
    const apiDiscountPrice = parsePriceValue(product.discount_price);
    if (apiDiscountPrice !== undefined) {
        return apiDiscountPrice;
    }

    const flashSaleProducts = product.promotion_flash_sale_product || [];
    const promotionProducts = product.promotion_product || [];

    const bestFlashSale = flashSaleProducts.reduce<PromotionFlashSaleInfo | undefined>((best, current) => {
        if (!best || (current.percent || 0) > (best.percent || 0)) {
            return current;
        }

        return best;
    }, undefined);

    if (bestFlashSale) {
        const flashSalePrice = parsePriceValue(bestFlashSale.price_flash_sale);
        if (flashSalePrice !== undefined) {
            return flashSalePrice;
        }

        if (bestFlashSale.percent) {
            return Math.round(originalPrice * (1 - bestFlashSale.percent / 100));
        }
    }

    const bestPromotion = promotionProducts.reduce<PromotionPriceInfo | undefined>((best, current) => {
        if (!best || (current.percent || 0) > (best.percent || 0)) {
            return current;
        }

        return best;
    }, undefined);

    if (bestPromotion) {
        const promotionPrice = parsePriceValue(bestPromotion.price_promotion);
        if (promotionPrice !== undefined) {
            return promotionPrice;
        }

        if (bestPromotion.percent) {
            return Math.round(originalPrice * (1 - bestPromotion.percent / 100));
        }
    }

    if (product.promotion?.percent) {
        return Math.round(originalPrice * (1 - product.promotion.percent / 100));
    }

    return undefined;
}

function normalizeCartProduct(product: CartProduct): CartProduct {
    const originalPrice = parsePriceValue(product.price) || 0;
    const computedDiscountPrice = resolveDiscountPrice(product, originalPrice);
    const discountPrice = computedDiscountPrice !== undefined && computedDiscountPrice < originalPrice
        ? computedDiscountPrice
        : undefined;
    const discountPercent = discountPrice !== undefined && originalPrice > 0
        ? Math.round((1 - discountPrice / originalPrice) * 100)
        : (product.promotion?.percent || product.discount || 0);

    return {
        ...product,
        price: originalPrice,
        discount_price: discountPrice,
        discount: discountPercent,
    };
}

function extractCartItemsResponse(data?: CartItemsResponse | CartItemsEnvelopeResponse): CartItemsResponse | undefined {
    if (!data) {
        return undefined;
    }

    const directData = data as CartItemsResponse;
    if (Array.isArray(directData.items)) {
        return directData;
    }

    const wrappedData = data as CartItemsEnvelopeResponse;
    if (wrappedData.data && Array.isArray(wrappedData.data.items)) {
        return wrappedData.data;
    }

    return undefined;
}

function normalizeCartItemsResponse(data?: CartItemsResponse | CartItemsEnvelopeResponse): CartItemsResponse {
    const payload = extractCartItemsResponse(data);
    if (!payload) {
        return { items: [], current_page: 0, total: 0 };
    }

    return {
        ...payload,
        items: (payload.items || []).map(item => ({
            ...item,
            product: item.product ? normalizeCartProduct(item.product) : item.product,
        })),
    };
}

async function listCartItems(page: number = 1, limit?: number): Promise<CartItemsResponse> {
    try {
        const response = await fetch(`${API_URL}/api/v1/fe/cards?page=${page}${limit ? `&limit=${limit}` : ''}`, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
        });

        if (!response.ok) {
            if (response.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('user');
                console.warn('Unauthorized access - perhaps the user is not logged in.');
                return { items: [], current_page: 0, total: 0 };
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        return normalizeCartItemsResponse(data);
    } catch (error) {
        console.error('Failed to fetch cart items:', error);
        return { items: [], current_page: 0, total: 0 }
    }
}

async function addItemToCart(product_id: string, quantity: number, showMessage: boolean = true, name?: string): Promise<void> {
    try {
        const data = new FormData();
        data.append('product_id', product_id);
        data.append('quantity', quantity.toString());
        const response = await fetch(API_URL + '/api/v1/fe/card', {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
            body: data,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
        console.error('Failed to add item to cart:', error);
    }
}

async function removeItemFromCart(cart_item_id: string): Promise<void> {
    try {
        const data = new FormData();
        data.append('cart_item_id', cart_item_id);
        const response = await fetch(API_URL + `/api/v1/fe/card/${cart_item_id}`, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
            body: data,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        console.log('Item removed from cart successfully');
        window.dispatchEvent(new Event('cart-updated'));
    } catch (error) {
        console.error('Failed to remove item from cart:', error);
    }
}

async function removeMultipleItemsFromCart(cart_item_ids: string[]): Promise<void> {
    try {
        const response = await fetch(API_URL + `/api/v1/fe/card_destroy_multi?ids=${cart_item_ids.join(',')}`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        window.dispatchEvent(new Event('cart-updated'));
        console.log('Multiple items removed from cart successfully');
    } catch (error) {
        console.error('Failed to remove multiple items from cart:', error);
    }
}

async function updateItemQuantity(cart_item_id: string, quantity: number): Promise<void> {
    try {
        const data = new FormData();
        data.append('quantity', quantity.toString());
        const response = await fetch(API_URL + `/api/v1/fe/cards/${cart_item_id}`, {
            method: 'PUT',
            headers: {
                'Accept': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
            },
            body: data,
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        window.dispatchEvent(new Event('cart-updated'));
        console.log('Item quantity updated successfully');
    } catch (error) {
        console.error('Failed to update item quantity:', error);
    }
}

export {
    listCartItems,
    addItemToCart,
    removeItemFromCart,
    removeMultipleItemsFromCart,
    updateItemQuantity,
};